import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useAtomValue } from 'jotai';
import { useLocation } from 'react-router-dom';
import { doesFocusableExist, getCurrentFocusKey } from '@noriginmedia/norigin-spatial-navigation';

import { activeFeatureIntrosAtom } from '@smart-tv/authenticated-app';
import { isErrorDialogOpenAtom, npvrFailedDialogResolvedAtom } from '@smart-tv/atoms';
import Dialog from '@smart-tv/components/dialog';
import useVodPlayer from '@smart-tv/components/player/vod-wrapper/_hooks/use-vod-player';
import useDialog from '@smart-tv/hooks/use-dialog';
import useFeatureIntros from '@smart-tv/hooks/use-feature-intros';
import useQuery from '@smart-tv/hooks/use-query';
import type { FeatureIntro as FeatureIntroData } from '@smart-tv/typings/content';
import { FeatureIntroLocation } from '@smart-tv/typings/content';

import FeatureIntroSlider from './feature-intro-slider';
import styles from './feature-intro.module.scss';

const FIRST_STRIPE_FIRST_ITEM_FOCUS_KEY = '0-0';
const FRONTPAGE_PATH = '/';
export const FeatureIntro = () => {
  const featureIntros = useAtomValue(activeFeatureIntrosAtom);
  const { getSeenFeatureIntroIds, markFeatureIntroAsSeen } = useFeatureIntros();
  const errorOpen = useAtomValue(isErrorDialogOpenAtom);
  const npvrFailedDialogResolved = useAtomValue(npvrFailedDialogResolvedAtom);
  const { pathname } = useLocation();
  const { programsheet } = useQuery<{ programsheet?: string }>();
  const { isPlaying } = useVodPlayer();
  const { handleSetDialogFocusKey } = useDialog();

  const [featureIntro, setFeatureIntro] = useState<FeatureIntroData | null>(null);
  // Only one intro per frontpage visit; reset when the user leaves the frontpage
  const shownThisVisitRef = useRef(false);
  const previousFocusKeyRef = useRef<string | null>(null);

  const isOnFrontpage = pathname === FRONTPAGE_PATH;
  const canShowFrontPageIntro =
    isOnFrontpage && !programsheet && !isPlaying && !errorOpen && npvrFailedDialogResolved;

  useEffect(() => {
    if (!isOnFrontpage) {
      shownThisVisitRef.current = false;
      setFeatureIntro(null);
      return;
    }
    if (!canShowFrontPageIntro || shownThisVisitRef.current || !featureIntros) return;

    const seenFeatureIntroIds = getSeenFeatureIntroIds();
    const nextIntro = featureIntros.find(
      (intro) =>
        intro.locationToShowIntro === FeatureIntroLocation.FRONTPAGE &&
        intro.steps.length > 0 &&
        !seenFeatureIntroIds.includes(intro.id)
    );
    if (!nextIntro) return;

    shownThisVisitRef.current = true;
    markFeatureIntroAsSeen(nextIntro.id);
    setFeatureIntro(nextIntro);
  }, [
    isOnFrontpage,
    canShowFrontPageIntro,
    featureIntros,
    getSeenFeatureIntroIds,
    markFeatureIntroAsSeen
  ]);

  const steps = featureIntro ? featureIntro.steps : [];
  const open = isOnFrontpage && !!featureIntro && !errorOpen;

  // Layout effekten bruges til at fange den tidligere fokus, før trinstknapperne får fokus
  useLayoutEffect(() => {
    if (!open) return;

    previousFocusKeyRef.current = getCurrentFocusKey();

    const rootElement = document.getElementById('root');
    if (rootElement) rootElement.classList.add(styles['root-blurred']);

    return () => {
      if (rootElement) rootElement.classList.remove(styles['root-blurred']);
    };
  }, [open]);

  const handleClose = useCallback(() => {
    if (doesFocusableExist(FIRST_STRIPE_FIRST_ITEM_FOCUS_KEY)) {
      handleSetDialogFocusKey(FIRST_STRIPE_FIRST_ITEM_FOCUS_KEY);
    } else {
      const previousFocusKey = previousFocusKeyRef.current;
      if (previousFocusKey && doesFocusableExist(previousFocusKey)) {
        handleSetDialogFocusKey(previousFocusKey);
      }
    }

    setFeatureIntro(null);
  }, [handleSetDialogFocusKey]);

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      showCloseButton={false}
      className={styles['feature-intro']}
      dialogContainerProps={{
        className: styles['feature-intro__container'],
        'aria-label': featureIntro ? featureIntro.name : undefined
      }}
    >
      <FeatureIntroSlider
        key={featureIntro ? featureIntro.id : undefined}
        steps={steps}
        onClose={handleClose}
      />
    </Dialog>
  );
};

export default FeatureIntro;
